import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';
import { FlagIcon, CheckCircleIcon, XCircleIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import styles from './ReportsPage.module.css';

/**
 * Receipts a member has told us are not theirs.
 *
 * A dispute is a billing complaint and a fraud signal at the same time — the
 * same report could mean a cashier scanned the wrong card, or that someone
 * else's card is being used. Either way it needs working through rather than
 * sitting in a support inbox, which is why it has its own queue.
 */

const STATUSES = ['open', 'investigating', 'resolved', 'rejected', 'all'];

const STATUS_LABELS = {
  open:          'Open',
  investigating: 'Investigating',
  resolved:      'Resolved',
  rejected:      'Rejected',
  all:           'All',
};

const STATUS_BADGE = {
  open:          'badge-warning',
  investigating: 'badge-warning',
  resolved:      'badge-active',
  rejected:      'badge-inactive',
};

// What the member picked, in the same words the app showed them.
const REASON_LABELS = {
  not_mine:     'Did not make this purchase',
  wrong_amount: 'Amount is wrong',
  wrong_items:  'Items are wrong',
  returned:     'Returned these items',
  other:        'Something else',
};

// What was concluded, not merely that someone finished looking.
const OUTCOME_LABELS = {
  removed:          'Removed from their history',
  amount_corrected: 'Amount corrected in BC',
  not_an_error:     'Purchase was genuine',
  no_action:        'No action taken',
};

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function ReportsPage() {
  const [rows, setRows]       = useState([]);
  const [status, setStatus]   = useState('open');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [note, setNote]       = useState('');

  const load = useCallback((s) => {
    setLoading(true);
    adminAPI.getDisputes(s)
      .then((res) => setRows(res.disputes || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  const act = async (id, newStatus, outcome) => {
    setBusy(id);
    try {
      await adminAPI.updateDispute(id, { status: newStatus, adminNote: note, outcome });
      setNote('');
      setExpanded(null);
      load(status);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Could not update the report.');
    } finally {
      setBusy(null);
    }
  };

  const openCount = rows.filter((r) => r.status === 'open').length;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Reported Purchases</h1>
          <p className={styles.sub}>
            {loading ? 'Loading…'
              : `${rows.length} report${rows.length === 1 ? '' : 's'}`
                + (status === 'open' ? ' awaiting review' : '')}
          </p>
        </div>
      </div>

      <div className={styles.filters}>
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`${styles.chip} ${status === s ? styles.chipActive : ''}`}
            onClick={() => setStatus(s)}
          >
            {STATUS_LABELS[s]}
            {s === 'open' && openCount > 0 && status === 'open' && (
              <span className={styles.count}>{openCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          <FlagIcon className={styles.emptyIcon} />
          <p className={styles.emptyTitle}>Nothing reported</p>
          <p className={styles.emptyBody}>
            {status === 'open'
              ? 'No member has flagged a purchase as not theirs.'
              : `No reports with the status “${STATUS_LABELS[status]}”.`}
          </p>
        </div>
      ) : (
        <div className={styles.list}>
          {rows.map((r) => {
            const open = expanded === r.id;
            const items = Array.isArray(r.items) ? r.items : [];
            return (
              <div key={r.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.who}>
                    <span className={styles.name}>
                      {[r.first_name, r.last_name].filter(Boolean).join(' ') || 'Member'}
                    </span>
                    <span className={styles.contact}>{r.mobile}{r.email ? ` · ${r.email}` : ''}</span>
                  </div>
                  <div className={styles.badges}>
                    {/* The outcome is what was decided; the status only says
                        whether anyone is still working on it. */}
                    {r.outcome && (
                      <span className={styles.outcomeBadge}>{OUTCOME_LABELS[r.outcome] || r.outcome}</span>
                    )}
                    <span className={`badge ${STATUS_BADGE[r.status] || ''}`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </div>
                </div>

                <div className={styles.grid}>
                  <div><span className={styles.label}>Reason</span>
                    <span className={styles.value}>{REASON_LABELS[r.reason] || r.reason}</span></div>
                  <div><span className={styles.label}>Receipt</span>
                    <span className={styles.mono}>{r.receipt_no}</span></div>
                  <div><span className={styles.label}>Purchased</span>
                    <span className={styles.value}>
                      {r.purchased_at ? format(new Date(r.purchased_at), 'dd MMM yyyy') : '—'}
                      {r.store_name || r.store_no ? ` · ${r.store_name || r.store_no}` : ''}
                    </span></div>
                  <div><span className={styles.label}>Amount</span>
                    <span className={styles.value}>{money(r.total)}</span></div>
                  <div><span className={styles.label}>Reported</span>
                    <span className={styles.value}>
                      {format(new Date(r.created_at), 'dd MMM yyyy HH:mm')}
                    </span></div>
                  <div><span className={styles.label}>BC account</span>
                    <span className={styles.mono}>{r.account_no}</span></div>
                </div>

                {r.note && (
                  <div className={styles.memberNote}>
                    <span className={styles.label}>What they said</span>
                    <p className={styles.noteText}>{r.note}</p>
                  </div>
                )}

                {r.admin_note && (
                  <div className={styles.adminNote}>
                    <span className={styles.label}>Internal note</span>
                    <p className={styles.noteText}>{r.admin_note}</p>
                  </div>
                )}

                {/* The receipt itself. A report is impossible to judge without
                    seeing what was actually bought. */}
                {items.length > 0 && (
                  <button className={styles.itemsToggle} onClick={() => setExpanded(open ? null : r.id)}>
                    <EyeIcon className={styles.toggleIcon} />
                    {open ? 'Hide items' : `Show ${items.length} item${items.length === 1 ? '' : 's'}`}
                  </button>
                )}

                {open && (
                  <div className={styles.items}>
                    {items.map((it, i) => (
                      <div key={`${r.id}-${i}`} className={styles.itemRow}>
                        <span className={styles.itemName}>
                          {it.quantity > 1 ? `${it.quantity} × ` : ''}{it.description || it.itemNo}
                        </span>
                        <span className={styles.itemAmount}>{money(it.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {(r.status === 'open' || r.status === 'investigating') && (
                  <div className={styles.actions}>
                    <input
                      className={styles.noteInput}
                      placeholder="Internal note (optional)"
                      value={expanded === r.id ? note : ''}
                      onFocus={() => setExpanded(r.id)}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    {/* Each button says what was decided and does it. A bare
                        "Resolve" left the member still looking at the receipt
                        they had reported. */}
                    <div className={styles.buttons}>
                      {r.status === 'open' && (
                        <button
                          className="btn-ghost"
                          disabled={busy === r.id}
                          onClick={() => act(r.id, 'investigating')}
                          title="Tell them someone is looking into it"
                        >
                          Still looking
                        </button>
                      )}
                      <button
                        className="btn-ghost"
                        disabled={busy === r.id}
                        onClick={() => act(r.id, 'rejected', 'not_an_error')}
                        title="The purchase was genuine — it stays in their history"
                      >
                        <XCircleIcon className={styles.btnIcon} />
                        Purchase was genuine
                      </button>
                      <button
                        className="btn-ghost"
                        disabled={busy === r.id}
                        onClick={() => act(r.id, 'resolved', 'amount_corrected')}
                        title="Staff corrected the invoice in Business Central; the next sync pulls the new total through"
                      >
                        <CheckCircleIcon className={styles.btnIcon} />
                        Amount corrected
                      </button>
                      <button
                        className="btn btn-primary"
                        disabled={busy === r.id}
                        onClick={() => {
                          if (window.confirm(
                            `Remove receipt ${r.receipt_no} from this member's history?

`
                            + 'They will stop seeing it in the app and be told it was removed. '
                            + 'Business Central keeps the original record, and this can be undone '
                            + 'by choosing a different outcome later.'
                          )) act(r.id, 'resolved', 'removed');
                        }}
                        title="Hide this receipt from the member"
                      >
                        <TrashIcon className={styles.btnIcon} />
                        Remove from their history
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
